import { useState } from "react";
import { AlertCircle, CheckCircle, Heart, Users, Clock, Layers, TrendingUp } from "lucide-react";
import { useNavigationStore } from "@/stores/navigation";
import DownloadForm from "@/components/common/DownloadForm";

export default function CaseStudyDanaFarber() {
  const navigationStore = useNavigationStore();
  const handleBookDemo = () => {
    navigationStore.openDemoModal();
  };
  const [downloadFormOpen, setDownloadFormOpen] = useState(false);
  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How Dana‑Farber's Cell Therapy Lab Boosted Throughput with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">Enterprise scheduling + triage transformed a complex, multi‑step manufacturing pipeline across 100+ protocols.</p>
              <div className="mt-8 flex gap-3">
                <button onClick={() => setDownloadFormOpen(true)} className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition">Download Full Case Study</button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            {/* Brand-forward hero panel (no images) */}
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">referral</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">scheduling</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">Modules implemented: Enterprise Schedule and Triage & E‑Referral.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK FACTS */}
      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Layers className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">100+ protocols</div>
              <div className="text-slate-600">Complex, multi‑step workflows</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Clock className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">5‑month build</div>
              <div className="text-slate-600">Went live Nov 2017</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">90% direct</div>
              <div className="text-slate-600">Self‑scheduled by clinical users</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <TrendingUp className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Higher capacity</div>
              <div className="text-slate-600">Previously unused slots filled</div>
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGE */}
      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">Coordinating cell‑based therapy scheduling via email and manual methods created bottlenecks and blind spots in lab capacity.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>No real‑time view of availability/capacity for requestors or lab managers</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Last‑minute changes left reserved slots unused; throughput suffered</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Complex, multi‑appointment protocols were hard to coordinate consistently</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Manual intake & email</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Limited capacity visibility</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Underutilized processing slots</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Complex protocol orchestration</span>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">SchedulingCX + ReferralCX were configured for cell therapy manufacturing with custom calendars, capacity controls, and automated infusion schedules.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
              <div className="font-semibold text-lg">Linked multi‑step procedures</div>
              <div className="mt-1 text-slate-600">Build protocols/trials as a series of appointments for one‑click orchestration.</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
              <div className="font-semibold text-lg">Capacity management</div>
              <div className="mt-1 text-slate-600">Control room, equipment, and technician availability with clinic functionality.</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
              <div className="font-semibold text-lg">Weekly calendar + processing time</div>
              <div className="mt-1 text-slate-600">Color‑coded views; auto‑calculates processing time by requesting group.</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
              <div className="font-semibold text-lg">Self‑service requests</div>
              <div className="mt-1 text-slate-600">Internal groups & partner hospitals submit directly; business rules route/authorize bookings.</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
              <div className="font-semibold text-lg">Queues & workload views</div>
              <div className="mt-1 text-slate-600">Technicians and managers see pipeline status at a glance.</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
              <div className="font-semibold text-lg">HL7 EMR integration</div>
              <div className="mt-1 text-slate-600">Demographics/admit/transfer data sync reduces manual entry and supports accurate infusion schedules.</div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS */}
      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>90% of procedures</strong> scheduled directly by clinical users without lab manager intervention.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Higher capacity</strong> by filling previously underutilized processing slots.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Increased volume & revenue</strong> from efficiency gains.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Faster response</strong> to patient availability via direct scheduling.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Less admin overhead</strong>—lab managers spend more time on lab work.</span></div>
          </div>
        </div>
      </section>

      {/* HUMAN IMPACT */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-semibold">Human Impact</h2>
            <p className="mt-4 text-slate-700">Patients and clinical teams benefit from clearer timelines, fewer reschedules, and faster access to therapy.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Clearer orchestration across appointments reduces patient stress.</span></li>
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Fewer last‑minute changes; smoother handoffs between lab and inpatient teams.</span></li>
            </ul>
          </div>
          <aside className="rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-6">
            <blockquote className="text-lg leading-relaxed">"Eceptionist gave us the visibility and control we needed to run at higher capacity without compromising safety."</blockquote>
            <div className="mt-3 text-slate-600">— Lab Leadership, Cell Manipulation Core Facility</div>
          </aside>
        </div>
      </section>

      {/* LOOKING BACK */}
      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">Configuring SchedulingCX and ReferralCX for cell therapy created a scalable blueprint for complex clinical manufacturing.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Speed to value</div><div className="text-slate-600 mt-1">Implemented in ~5 months; live Nov 2017</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Scalable orchestration</div><div className="text-slate-600 mt-1">Works across 100+ active protocols</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Measurable impact</div><div className="text-slate-600 mt-1">Higher capacity, less admin, better experience</div></div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Ready to transform patient access and care coordination?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX can streamline complex workflows across your network.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />

      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="Dana-Farber Cell Therapy Case Study"
        pdfPath="/PDF/Detailed Case Studies/DFCI_CaseStudyFinal.pdf"
      />
    </main>
  );
}
